<script lang="ts">
  import { PUBLIC_API_URL } from '$env/static/public';

  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/stores';

  import { onMount } from 'svelte';

  import { get } from 'svelte/store';

  import { Order } from '@shared/types';
  import { ClientOnly } from '@shared/ui/client-only';
  import { Container } from '@shared/ui/container';
  import { GridToggler } from '@shared/ui/grid-toggler';
  import { Heading } from '@shared/ui/heading';
  import { is, parseSlug, serializeSearchParams } from '@shared/utils';

  import type { ArtAssetReadManyDto } from '@entities/art-asset';
  import type { ArtistType } from '@entities/artist';
  import type { AuctionListItem } from '@entities/auction';
  // TODO: Uncomment this when DCF-3280 will be done (planned for 29 sprint)
  // import type { SelectionsReadManyResponse } from '@entities/selection';

  import { ArtworksFilter } from '@features/artwork/artworks-filter';
  import { ArtworksSort } from '@features/artwork/artworks-sort';
  import { Pagination } from '@features/pagination';
  import { reportConfig } from '@features/report';

  import { ArtworkList } from '@widgets/artwork-list';
  import { ArtistArtworksFilterSidebar, filtersLibs } from '@widgets/artworks-filter-sidebar';
  import { AuctionList } from '@widgets/auction-list';
  // import { SelectionList } from '@widgets/selection-list';

  import { ARTWORKS_PER_PAGE } from '@pages/artworks';

  import { artworksSortOptions, defaultSortParams } from '../configs';
  import ArtistInfo from './artist-info.svelte';

  export let artist: ArtistType;
  export let auctions: AuctionListItem[];
  export let artworks: ArtAssetReadManyDto['art_assets'];
  export let artworksCount: number;
  export let pageNumber: number;
  export let loadMore: number;
  export let auctionsCount: number;
  export let form: reportConfig.ReportForm;
  export let hasParams: boolean;
  // export let selectionsCount: number;
  // export let selections: SelectionsReadManyResponse['selections'];

  const defaultParams = {
    column: 'year',
    order: 'desc',
  } as const;
  const limit = ARTWORKS_PER_PAGE;

  let container: HTMLElement;
  let prevPage = pageNumber;
  let loading = false;
  let isMounted = false;

  let sortField = $page.url.searchParams.get('column') || defaultSortParams.column;
  let sortOrder: Order = ($page.url.searchParams.get('order') as Order) || defaultSortParams.order;

  $: subtitle = `${artist.name} artworks in Digital Original Format`;
  // $: selectionsSubtitle = `${artist.name} artworks in Selections`;

  async function requestRedirect(params: { page: number; loadMore?: number; invalidateAll?: boolean }) {
    const { url, params: pageParams } = get(page);

    const searchParams = configureSearchParams(params);

    const newPage = params?.page || 1;
    const isSamePage = params.page === prevPage;

    url.searchParams.set('order', `${searchParams.get('order')}`);
    url.searchParams.set('column', `${searchParams.get('column')}`);

    if (!params.loadMore) {
      url.searchParams.delete('loadMore');
    }

    const searchParamsString = serializeSearchParams(url.searchParams);

    if (isSamePage) {
      await goto(searchParamsString, {
        noScroll: isSamePage,
        replaceState: true,
        invalidateAll: true,
      });
    } else {
      const filters = filtersLibs.getFiltersFromSlug(pageParams.slug);

      const artistUrl = `/artist-${artist.id}-${artist.slug}`;

      const newUrl = `${is(newPage !== 1, `${artistUrl}/${filters}page-${newPage}`, `${artistUrl}/${filters}`)}${searchParamsString}`;

      prevPage = pageNumber;

      await goto(newUrl, { invalidateAll: params.invalidateAll ?? false, noScroll: true });
    }
  }

  async function handleLoadMore(params: { page: number; loadMore?: number }) {
    const { url } = get(page);

    url.searchParams.set('loadMore', `${params.loadMore}`);

    const searchParams = configureSearchParams(params);

    const someReceivedArtworks = await handleLoadArtworks(searchParams);

    artworks = [...artworks, ...someReceivedArtworks];

    await goto(`?${url.searchParams.toString()}`, {
      noScroll: true,
      replaceState: true,
      invalidateAll: false,
    });
  }

  async function handleLoadArtworks(params: URLSearchParams) {
    const searchParams = params.toString();

    try {
      const resp: any = await fetch(`${PUBLIC_API_URL}/art-assets?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { art_assets } = await resp.json();

      return art_assets;
    } catch (error) {
      console.error('Error fetching artworks', error);

      return [];
    }
  }

  function configureSearchParams(params: { page: number; loadMore?: number }) {
    const { params: pageParams } = get(page);
    const parsedParams = parseSlug(pageParams.slug);

    const preparedParams = {
      offset: `${limit * (params.page + (params.loadMore ?? 0) - 1)}`,
      limit: `${limit}`,
      column: sortField,
      order: sortOrder,
      institutions: $page.data.configurations.id,
      artists: `${artist.id}`,
      ...parsedParams,
    };

    const searchParams = new URLSearchParams(preparedParams);

    return searchParams;
  }

  const applySort = async (field: string, order: string) => {
    if (!isMounted || !field || !order) return;

    loading = true;
    loadMore = 0;

    await requestRedirect({ page: 1, loadMore, invalidateAll: true });

    loading = false;
  };

  $: applySort(sortField, sortOrder);

  onMount(() => {
    isMounted = true;
  });

  afterNavigate(({ to }) => {
    if (to?.url.searchParams?.size === 0) {
      sortField = defaultParams.column;
      sortOrder = defaultParams.order;
    }
  });
</script>

<div class="wrapper-container">
  <ArtistInfo {artist} {form} />
</div>

{#if auctionsCount}
  <Heading
    containerClass="mb-0 md:mb-0 lg:mb-0 lg:mt-12 md:mt-12"
    dividePosition="none"
    title="Auctions"
    titleClass="inline text-balance text-md uppercase md:text-base lg:text-base"
    counter="{auctionsCount}"
  />

  <Container containerClass="py-8 md:py-12">
    <AuctionList {auctions} />
  </Container>
{/if}

{#if artworksCount}
  <div bind:this="{container}">
    <Heading
      containerClass="mb-0 md:mb-0 lg:mb-0 lg:mt-12 md:mt-12"
      title="{subtitle}"
      titleClass="inline text-balance text-lg md:text-lg lg:text-lg"
      wrapperClass="flex flex-col items-start gap-6 md:gap-8 lg:items-end lg:gap-12 lg:flex-row normal-case"
      dividePosition="none"
      tag="h2"
      counter="{artworksCount}"
    >
      <div class="flex items-center gap-9 self-start lg:ml-0 lg:self-end">
        {#if hasParams || artworksCount > 10}
          <!-- show filter only for more than 10 artworks and in case if filters applied (that means that total is already was > 10) -->
          <ArtworksFilter />
        {/if}
        <div class="w-[168px]">
          <ArtworksSort bind:field="{sortField}" bind:order="{sortOrder}" sortOptions="{artworksSortOptions}" />
        </div>

        <GridToggler />
      </div>
    </Heading>
  </div>

  <Container containerClass="py-8 md:py-12">
    <Pagination
      isFullWidth
      containerToScroll="{container}"
      pageSize="{limit}"
      requestFunction="{requestRedirect}"
      loadMoreFunction="{handleLoadMore}"
      bind:currentPage="{pageNumber}"
      bind:loadMore
      bind:totalDataCount="{artworksCount}"
      bind:loading
    >
      <ArtworkList {artworks} />
    </Pagination>
  </Container>
{/if}

<!-- TODO: Uncomment this when DCF-3280 will be done (planned for 29 sprint) -->
<!-- {#if selectionsCount}
  <Heading
    containerClass="mb-0 md:mb-0 lg:mb-0 lg:mt-12 md:mt-12"
    dividePosition="none"
    title="{selectionsSubtitle}"
    titleClass="inline text-balance text-lg md:text-lg lg:text-lg"
    counter="{selectionsCount}"
    link="selections"
    linkText="See all Selections"
    linkClass="normal-case"
    wrapperClass="flex-col gap-3 items-start md:flex-row md:items-center"
  />
  <Container containerClass="py-8 md:py-12">
    <SelectionList list="{selections}" />
  </Container>
{/if} -->

<ClientOnly>
  <ArtistArtworksFilterSidebar {artist} />
</ClientOnly>
